import type { InspectionItem, ReportLanguage } from '../../types';

/**
 * Reine Helfer der Mängelliste (InspectionList, InspectionItemCard): Fotos und
 * Beweisbild nach dem Hochladen, KI-Übernahme mit „Rückgängig“, Rückfrage
 * „Foto löschen?“. Ohne React; Netz nur über übergebene Funktionen — deshalb
 * einzeln testbar.
 */

/** Höchstzahl Fotos je Eintrag (wie im Mangel-Assistenten). */
export const MAX_FOTOS = 3;

/** Fotoliste eines Eintrags — auch für Altbestand ohne `photos` (JSON-Import). */
export const fotosVon = (item: Pick<InspectionItem, 'photos'>): string[] =>
    (Array.isArray(item.photos) ? item.photos : []);

/**
 * Wie viele Fotos passen noch dazu? Laufende Uploads zählen mit — sonst ließe
 * sich während des Hochladens ein viertes Foto auswählen.
 */
export const freieFotoPlaetze = (vorhanden: number, hochladend: number, max: number = MAX_FOTOS): number =>
    Math.max(0, max - vorhanden - hochladend);

/**
 * Hochgeladene Fotos an den AKTUELLEN Stand des Eintrags hängen. Der Upload
 * dauert; was in der Zwischenzeit eingegeben wurde, bleibt stehen — ergänzt
 * wird nur die Fotoliste. Fehlt der Eintrag (gelöscht) oder kam nichts an,
 * kommt dieselbe Liste zurück: dann gibt es nichts zu speichern.
 */
export function fotosAnhaengen(items: InspectionItem[], id: string, fotos: readonly string[]): InspectionItem[] {
    if (fotos.length === 0 || !items.some(i => i.id === id)) return items;
    return items.map(i => (i.id === id ? { ...i, photos: [...(i.photos || []), ...fotos] } : i));
}

// ─── Späte Ergebnisse (Upload, KI) ───────────────────────────────────────────

/**
 * Ändert die Mängelliste auf dem Stand, der BEIM AUFRUF gilt. Der Aufrufer
 * (Begehungsmodul) ruft `aendern` sofort auf, mit den Einträgen seines
 * aktuellen Berichts — das wirkt auch, wenn die Liste inzwischen abgebaut ist.
 */
export type ItemsAendern = (aendern: (aktuell: InspectionItem[]) => InspectionItem[]) => void;

/** Wohin ein spätes Ergebnis geschrieben wird — erst bei der Antwort gelesen. */
export interface SchreibWeg {
    aendern?: ItemsAendern;
    /** Rückfall ohne `aendern`: Stand und onChange des letzten Renders der Liste. */
    items: InspectionItem[];
    melden: (items: InspectionItem[]) => void;
    /** Steht die Liste noch? Nur dann ist `items` der aktuelle Stand. */
    steht: boolean;
}

/**
 * Ein spätes Ergebnis auf den Stand anwenden, der JETZT gilt. Ohne `aendern`
 * nur, solange die Liste steht: danach trüge `items` einen alten Bericht, und
 * das Schreiben risse heraus, was seitdem dazugekommen ist.
 */
export function spaetSchreiben(weg: SchreibWeg, aenderung: (aktuell: InspectionItem[]) => InspectionItem[]): void {
    if (weg.aendern) {
        weg.aendern(aenderung);
        return;
    }
    if (!weg.steht) return;
    const neu = aenderung(weg.items);
    if (neu !== weg.items) weg.melden(neu);
}

/**
 * Fotos hochladen, dann anhängen. Der Schreibweg wird erst NACH dem Hochladen
 * gelesen (`weg()`), nicht beim Start. Scheitert ein Upload, bleibt das
 * verkleinerte Bild — der Speicherweg lädt es selbst nach.
 */
export async function fotosHochladenUndAnhaengen(
    id: string,
    base64s: readonly string[],
    hochladen: ((base64: string) => Promise<string>) | null,
    weg: () => SchreibWeg,
): Promise<void> {
    const fertig = await Promise.all(base64s.map(async (base64) => {
        if (!hochladen) return base64;
        try {
            return await hochladen(base64);
        } catch (e) {
            console.error('Upload failed, falling back to base64', e);
            return base64;
        }
    }));
    spaetSchreiben(weg(), aktuell => fotosAnhaengen(aktuell, id, fertig));
}

// ─── Beweisbild im Freimelde-Dialog ──────────────────────────────────────────

/** Der Teil des Freimelde-Dialogs, den das Beweisbild braucht. */
export interface BeweisbildDialog {
    isOpen: boolean;
    /** Kennung dieser Öffnung des Dialogs (jede Öffnung eine neue). */
    sitzung: number;
    image?: string;
}

/**
 * Beweisbild in den AKTUELLEN Dialogstand setzen (funktionales Update): Datum
 * und Kommentar von zwischendurch bleiben. Ist der Dialog zu oder inzwischen
 * neu geöffnet — auch für denselben Mangel —, bleibt alles, wie es ist: ein
 * spätes Bild öffnet nichts wieder und verdrängt kein neueres.
 */
export function beweisbildEinsetzen<T extends BeweisbildDialog>(stand: T, sitzung: number, bild: string): T {
    if (!stand.isOpen || stand.sitzung !== sitzung) return stand;
    return { ...stand, image: bild };
}

/**
 * Nach dem Hochladen ersetzt die URL das verkleinerte Bild — nur, solange der
 * Dialog genau dieses Bild zeigt (nicht entfernt, nicht ausgetauscht).
 */
export function beweisbildUrlNachtragen<T extends BeweisbildDialog>(stand: T, sitzung: number, base64: string, url: string): T {
    if (!stand.isOpen || stand.sitzung !== sitzung || stand.image !== base64 || url === base64) return stand;
    return { ...stand, image: url };
}

/**
 * Wurde vor dem Ende des Uploads abgeschlossen, trägt der Eintrag das
 * verkleinerte Bild: gegen die URL tauschen (derselbe Inhalt, nur nicht mehr
 * im Bericht selbst). Sonst dieselbe Liste.
 */
export function beweisbildUrlAmEintrag(items: InspectionItem[], id: string, base64: string, url: string): InspectionItem[] {
    const traegt = (i: InspectionItem) => i.id === id && i.closedImage === base64;
    if (url === base64 || !items.some(traegt)) return items;
    return items.map(i => (traegt(i) ? { ...i, closedImage: url } : i));
}

// ─── KI-Vorschlag ────────────────────────────────────────────────────────────

/** Die drei Felder, die der KI-Vorschlag setzt. */
export type KiTexte = Pick<InspectionItem, 'title' | 'observation' | 'regulation'>;

/** Einstufiges „Rückgängig“ nach einem KI-Vorschlag. */
export interface KiRueckgaengig {
    id: string;
    vorher: KiTexte;
    nachher: KiTexte;
}

/** Meldung, wenn die KI scheitert und keinen eigenen Text mitliefert. */
export const KI_FEHLER_TEXT = 'KI nicht erreichbar. Bitte selbst formulieren oder später erneut versuchen.';
/** Dieselbe Meldung in englischen Berichten (wie im Fenster „Textvorschlag“). */
export const KI_FEHLER_TEXT_EN = 'AI not reachable. Please write the text yourself or try again later.';

const kiTexteVon = (t: KiTexte): KiTexte => ({ title: t.title, observation: t.observation, regulation: t.regulation });

const gleicheTexte = (a: KiTexte, b: KiTexte): boolean =>
    a.title === b.title && a.observation === b.observation && a.regulation === b.regulation;

/**
 * KI-Texte in den AKTUELLEN Stand übernehmen (die Anfrage dauert Sekunden;
 * Ort, Firma, Fotos von zwischendurch bleiben). Liefert die neue Liste und den
 * Stand für „Rückgängig“ — oder null, wenn der Eintrag inzwischen fehlt.
 */
export function kiTexteAnwenden(
    items: InspectionItem[],
    id: string,
    texte: KiTexte,
): { items: InspectionItem[]; rueckgaengig: KiRueckgaengig } | null {
    const eintrag = items.find(i => i.id === id);
    if (!eintrag) return null;
    const nachher = kiTexteVon(texte);
    return {
        items: items.map(i => (i.id === id ? { ...i, ...nachher } : i)),
        rueckgaengig: { id, vorher: kiTexteVon(eintrag), nachher },
    };
}

/**
 * KI-Vorschlag holen und auf den Stand bei der ANTWORT übernehmen. Liefert den
 * Stand für „Rückgängig“ — null, wenn nichts übernommen wurde (Eintrag weg,
 * Liste abgebaut). Scheitert `holen`, wirft es weiter: die Karte zeigt die
 * Meldung, geschrieben wird nichts.
 */
export async function kiVorschlagUebernehmen(
    id: string,
    holen: () => Promise<KiTexte>,
    weg: () => SchreibWeg,
): Promise<KiRueckgaengig | null> {
    const texte = await holen();
    const ergebnis: { rueckgaengig: KiRueckgaengig | null } = { rueckgaengig: null };
    spaetSchreiben(weg(), aktuell => {
        const r = kiTexteAnwenden(aktuell, id, texte);
        ergebnis.rueckgaengig = r ? r.rueckgaengig : null;
        return r ? r.items : aktuell;
    });
    return ergebnis.rueckgaengig;
}

/**
 * „Rückgängig“ gilt nur, solange der Eintrag noch genau den KI-Text trägt.
 * Wer danach selbst geändert hat, verlöre sonst seine Änderung. Hat die KI
 * nichts verändert, gibt es auch nichts zurückzunehmen.
 */
export function kiRueckgaengigMoeglich(stand: KiRueckgaengig | null, item: InspectionItem): boolean {
    if (!stand || stand.id !== item.id) return false;
    return gleicheTexte(item, stand.nachher) && !gleicheTexte(stand.vorher, stand.nachher);
}

/**
 * Anzeigbarer Text zu einem KI-Fehler: die Meldung des Dienstes, sonst der
 * Standardtext. In englischen Berichten der englische Standardtext.
 */
export function kiFehlerText(fehler: unknown, sprache: ReportLanguage = 'de'): string {
    if (sprache === 'en') return KI_FEHLER_TEXT_EN;
    return fehler instanceof Error && fehler.message.trim() ? fehler.message : KI_FEHLER_TEXT;
}

// ─── Rückfrage „Foto löschen?“ ────────────────────────────────────────────────

/** So lange steht „Foto löschen?“ an der Kachel, dann verfällt die Rückfrage (wie im Assistenten). */
export const LOESCHFRAGE_FRIST_MS = 3000;

/** Offene Rückfrage: Index UND Bild — rückt die Liste, trifft sie nicht das falsche Foto. */
export interface FotoLoeschFrage {
    index: number;
    foto: string;
}

export const loeschFrageGilt = (frage: FotoLoeschFrage | null, fotos: readonly string[], index: number): boolean =>
    !!frage && frage.index === index && fotos[index] === frage.foto;
